import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { collection, getDocs, doc, getDoc, updateDoc, serverTimestamp, query, where, orderBy } from 'firebase/firestore';

function FlagManagement() {
  const [flaggedQuestions, setFlaggedQuestions] = useState([]);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [jumpToPage, setJumpToPage] = useState('');

  useEffect(() => {
    fetchFlaggedQuestions();
  }, []);

  const fetchFlaggedQuestions = async () => {
    console.log('Attempting to fetch flagged questions...');
    try {
      const flaggedQuery = query(
        collection(db, 'flagged_questions'),
        where('status', '==', 'pending'),
        orderBy('timestamp', 'desc')
      );
      const flaggedSnapshot = await getDocs(flaggedQuery);
      
      const flaggedList = [];
      for (const docSnapshot of flaggedSnapshot.docs) {
        const flagData = { id: docSnapshot.id, ...docSnapshot.data() };
        
        if (flagData.questionId) {
          const questionRef = doc(db, 'questions', flagData.questionId);
          const questionDoc = await getDoc(questionRef);
          if (questionDoc.exists()) {
            flaggedList.push({ ...flagData, questionDetails: questionDoc.data() });
          } else {
            console.warn(`Question with ID ${flagData.questionId} not found for flag ${flagData.id}`);
            flaggedList.push(flagData);
          }
        } else {
          console.warn(`Flag ${flagData.id} is missing questionId`);
          flaggedList.push(flagData);
        }
      }

      console.log('Fetched flagged questions:', flaggedList.length, 'documents');
      setFlaggedQuestions(flaggedList);
      setTotalPages(Math.ceil(flaggedList.length / itemsPerPage));
    } catch (error) {
      console.error('Error fetching flagged questions:', error);
    }
  };

  const handleFlagAction = async (flagId, action) => {
    try {
      const flagRef = doc(db, 'flagged_questions', flagId);
      await updateDoc(flagRef, {
        status: action === 'approve' ? 'approved' : 'rejected',
        reviewedBy: auth.currentUser.uid,
        reviewedAt: serverTimestamp()
      });
      
      await fetchFlaggedQuestions();
      setShowFlagModal(false);
      setSelectedFlag(null);
    } catch (error) {
      console.error('Error handling flag action:', error);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setJumpToPage('');
  };

  const handleJumpToPage = (e) => {
    e.preventDefault();
    const pageNumber = parseInt(jumpToPage);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setJumpToPage('');
    }
  };

  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return flaggedQuestions.slice(startIndex, endIndex);
  };

  return (
    <section className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-2xl font-semibold mb-4">Flagged Questions</h2>
      
      {flaggedQuestions.length === 0 ? (
        <p className="text-gray-500">No flagged questions to review.</p>
      ) : (
        <>
          <div className="space-y-4">
            {getCurrentPageItems().map(flag => (
              <div key={flag.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-4">
                  <div className="space-y-2">
                    {flag.questionDetails ? (
                      <>
                        <div className="flex items-start gap-2">
                          <span className="font-medium">Question:</span>
                          <span className="text-gray-700">{flag.questionDetails.question}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Subject:</span>
                          <span className="text-gray-600">{flag.questionDetails.subject}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Question ID:</span>
                        <span className="text-gray-600">{flag.questionId} (Details unavailable)</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Reason:</span>
                      <span className="text-gray-600">{flag.reason}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Flagged on:</span>
                      <span className="text-gray-600">
                        {flag.timestamp?.toDate().toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedFlag(flag);
                        setShowFlagModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Review
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Next
              </button>
            </div>

            <form onSubmit={handleJumpToPage} className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max={totalPages}
                value={jumpToPage}
                onChange={(e) => setJumpToPage(e.target.value)}
                placeholder="Go to page"
                className="w-20 px-2 py-1 border rounded"
              />
              <button
                type="submit"
                className="px-3 py-1 bg-[rgb(130,88,18)] text-white rounded hover:bg-[rgb(110,68,0)]"
              >
                Go
              </button>
            </form>
          </div>
        </>
      )}

      {/* Flag Review Modal */}
      {showFlagModal && selectedFlag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full relative">
            <button
              onClick={() => {
                setShowFlagModal(false);
                setSelectedFlag(null);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-xl font-semibold mb-4">Review Flagged Question</h3>
            
            <div className="mb-6">
              <h4 className="font-medium mb-2">Question Details</h4>
              <div className="space-y-2">
                <p className="text-gray-600">
                  <span className="font-medium">Question ID:</span> {selectedFlag.questionId}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Quiz ID:</span> {selectedFlag.quizId}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Question Number:</span> {selectedFlag.questionNumber}
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <h4 className="font-medium mb-2">Flag Details</h4>
              <div className="space-y-2">
                <p className="text-gray-600">
                  <span className="font-medium">Reason:</span> {selectedFlag.reason}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Flagged by:</span> {selectedFlag.userId}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Flagged on:</span>{' '}
                  {selectedFlag.timestamp.toDate().toLocaleString()}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">User's Answer:</span> {selectedFlag.userAnswer}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Correct Answer:</span> {selectedFlag.correctAnswer}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => handleFlagAction(selectedFlag.id, 'reject')}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Reject Flag
              </button>
              <button
                onClick={() => handleFlagAction(selectedFlag.id, 'approve')}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Approve Flag
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default FlagManagement; 